<script setup lang="ts">
import { computed, onMounted, type Ref, ref } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import { AxiosError } from 'axios';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import {
  type EdgeDevice,
  EMPTY_NEW_EDGE_DEVICE,
  ModelTypeEnum,
  type NewEdgeDevice,
} from '@/models/EdgeDevice';
import {
  createEdgeDevice,
  loadEdgeDevices,
  patchEdgeDevice,
  deleteEdgeDevice,
} from '@/api/EdgeDeviceApi';
import formatDate from '@/helpers/DateFormatterHelper';
import {
  requiredRule,
  selectFieldRequiredRule,
  serialNumberRegexRule,
} from '@/helpers/FormRules';
import useAuthStore from '@/store/AuthStore';
import { Permission } from '@/models/Roles';
import { useAlertsStore } from '@/store/AlertsStore';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();
const authStore = useAuthStore();
const alerts = useAlertsStore();

const devices: Ref<EdgeDevice[]> = ref([]);
const selectedDevice: Ref<EdgeDevice | null> = ref(null);
const editedDevice: Ref<EdgeDevice | null> = ref(null);
const deviceToDelete: Ref<EdgeDevice | null> = ref(null);
const detailDialog = ref(false);
const editDialog = ref(false);
const isEditing = ref(false);
const createDialog = ref(false);
const deleteDialog = ref(false);
const searchQuery = ref('');
const newDevice: Ref<NewEdgeDevice> = ref(cloneDeep(EMPTY_NEW_EDGE_DEVICE));

const createFormValid = ref(false);
const editFormValid = ref(true);

async function getDevices() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    devices.value = await loadEdgeDevices();
  } catch (err) {
    alerts.error($gettext('Failed to load edge devices'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

async function createDevice(device: NewEdgeDevice) {
  try {
    await createEdgeDevice(device);
    alerts.success($gettext('Edge device created'));
    getDevices();
    closeCreateDialog();
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 409) {
      alerts.error(
        $gettext(
          'Another edge device with the same serial number already exists',
        ),
      );
    } else {
      alerts.error($gettext('Failed to create edge device'));
    }
    console.error(err);
  }
}

function closeEditDialog() {
  editedDevice.value = null;
  editDialog.value = false;
  isEditing.value = false;
}

async function saveChanges() {
  if (editedDevice.value) {
    try {
      await patchEdgeDevice(editedDevice.value);
      alerts.success($gettext('Edge device updated'));
      getDevices();
      closeEditDialog();
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 409) {
        alerts.error(
          $gettext(
            'Another edge device with the same serial number already exists',
          ),
        );
      } else {
        alerts.error($gettext('Failed to update edge device'));
      }
      console.error(err);
    }
  }
}

function openDetailDialog(device: EdgeDevice) {
  if (!isEditing.value) {
    selectedDevice.value = device;
    detailDialog.value = true;
  }
  editedDevice.value = cloneDeep(device);
}

function openCreateDialog() {
  newDevice.value = cloneDeep(EMPTY_NEW_EDGE_DEVICE);
  createDialog.value = true;
}

function enableEdit(device: EdgeDevice | null) {
  selectedDevice.value = device;
  editedDevice.value = cloneDeep(device);
  detailDialog.value = false;
  editDialog.value = true;
  isEditing.value = true;
}

function closeDetailDialog() {
  detailDialog.value = false;
  selectedDevice.value = null;
}

function closeCreateDialog() {
  createDialog.value = false;
}

async function enableDelete(device: EdgeDevice | null) {
  deviceToDelete.value = device;
  deleteDialog.value = true;
}

function closeDeleteDialog() {
  deleteDialog.value = false;
  deviceToDelete.value = null;
}

async function confirmDelete() {
  if (deviceToDelete.value) {
    try {
      await deleteEdgeDevice(deviceToDelete.value);
      alerts.success($gettext('Edge device deleted'));
      getDevices();
      closeDeleteDialog();
    } catch (err) {
      alerts.error($gettext('Failed to delete edge device'));
      console.error(err);
    }
  }
}

const filteredDevices = computed(() => {
  const query = (searchQuery.value || '').toLowerCase();
  return devices.value.filter((device) => {
    const nameMatch = (device.name || '').toLowerCase().includes(query);
    const serialNumberMatch = (device.serialNumber || '')
      .toLowerCase()
      .includes(query);
    const modelTypeMatch = (device.modelType || '')
      .toLowerCase()
      .includes(query);

    return nameMatch || serialNumberMatch || modelTypeMatch;
  });
});

const headers = computed(() => {
  const baseHeaders: object[] = [
    { title: $gettext('Name'), value: 'name', sortable: true },
    {
      title: $gettext('Serial number'),
      value: 'serialNumber',
      sortable: true,
    },
    {
      title: $gettext('Model type'),
      value: 'modelType',
      sortable: true,
    },
    {
      title: $gettext('Created at'),
      value: 'createdAt',
      sortable: true,
    },
  ];

  if (authStore.hasPermission(Permission.PATCH_EDGE_DEVICE)) {
    baseHeaders.push({
      title: $gettext('Edit'),
      key: 'edit',
      sortable: false,
      width: '100px',
    });
  }

  if (authStore.hasPermission(Permission.DELETE_EDGE_DEVICE)) {
    baseHeaders.push({
      title: $gettext('Delete'),
      key: 'delete',
      sortable: false,
      width: '100px',
    });
  }

  return baseHeaders;
});

onMounted(() => {
  getDevices();
});
</script>
<template>
  <div class="pa-4 pa-sm-12">
    <v-row>
      <v-col cols="12">
        <h1>
          {{ $gettext('Edge Devices') }}
          <v-btn
            size="small"
            class="mb-1 ml-4 gradient-oceanblue-whitesands"
            icon="mdi-cached"
            @click="getDevices"
          />
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mb-4">
        <v-row
          v-if="authStore.hasPermission(Permission.POST_EDGE_DEVICE)"
          class="ma-4 ml-0"
        >
          <r-btn variant="outlined" @click="openCreateDialog">{{
            $gettext('Add Edge Device')
          }}</r-btn>
        </v-row>
        <v-row class="mt-12">
          <v-col cols="12" md="8" lg="6" xl="4">
            <v-text-field
              v-model="searchQuery"
              label="Search"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              items-per-page="25"
              :items="filteredDevices"
              :headers="headers"
              @click:row="
                (event: Event, item: any) => openDetailDialog(item.item)
              "
            >
              <template #item.createdAt="{ item }">
                {{ formatDate(item.createdAt, true) }}
              </template>
              <template #item.edit="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-pencil"
                        @click.stop="enableEdit(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Edit') }}
                </v-tooltip>
              </template>
              <template #item.delete="{ item }">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        icon="mdi-trash-can-outline"
                        @click.stop="enableDelete(item)"
                      ></v-icon>
                    </span>
                  </template>
                  {{ $gettext('Delete') }}
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-dialog v-model="createDialog" max-width="600">
      <v-card>
        <v-card-title>{{ $gettext('Add Edge Device') }}</v-card-title>
        <v-card-text>
          <v-form v-model="createFormValid">
            <v-text-field
              v-model="newDevice.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-text-field
              v-model="newDevice.serialNumber"
              :label="$gettext('Serial number')"
              :rules="[requiredRule, serialNumberRegexRule]"
            ></v-text-field>
            <v-select
              v-model="newDevice.modelType"
              :items="Object.values(ModelTypeEnum)"
              :label="$gettext('Model type')"
              :rules="[selectFieldRequiredRule]"
            ></v-select>
            <v-textarea
              v-model="newDevice.internalNotes"
              :label="$gettext('Internal Notes')"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeCreateDialog()">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!createFormValid"
            @click="createDevice(newDevice)"
            >{{ $gettext('Add') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="selectedDevice"
      v-model="detailDialog"
      max-width="600"
      @click:outside="closeDetailDialog"
    >
      <v-card>
        <v-card-title>{{ $gettext('Edge Device') }}</v-card-title>
        <v-card-text>
          <div>
            <p>
              {{ $gettext('Name') }}:
              <span class="font-weight-bold">{{ selectedDevice.name }}</span>
            </p>
            <p>
              {{ $gettext('Serial number') }}:
              <span class="font-weight-bold">{{
                selectedDevice.serialNumber
              }}</span>
            </p>
            <p>
              {{ $gettext('Model type') }}:
              <span class="font-weight-bold">{{
                selectedDevice.modelType
              }}</span>
            </p>
            <p>
              {{ $gettext('Internal Notes') }}:
              <span class="font-weight-bold">{{
                selectedDevice.internalNotes
              }}</span>
            </p>
            <p>
              {{ $gettext('Created at') }}:
              <span class="font-weight-bold">{{
                formatDate(selectedDevice.createdAt, true)
              }}</span>
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeDetailDialog">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            v-if="authStore.hasPermission(Permission.PATCH_EDGE_DEVICE)"
            color="primary"
            @click="enableEdit(selectedDevice)"
            >{{ $gettext('Edit') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="editedDevice"
      v-model="editDialog"
      max-width="600"
      @click:outside="closeEditDialog"
    >
      <v-card>
        <v-card-title>{{ $gettext('Edge Device') }}</v-card-title>
        <v-card-text>
          <v-form v-model="editFormValid">
            <v-text-field
              v-model="editedDevice.name"
              :label="$gettext('Name')"
              :rules="[requiredRule]"
            ></v-text-field>
            <v-text-field
              v-model="editedDevice.serialNumber"
              :label="$gettext('Serial number')"
              :rules="[requiredRule, serialNumberRegexRule]"
            ></v-text-field>
            <v-select
              v-model="editedDevice.modelType"
              :items="Object.values(ModelTypeEnum)"
              :label="$gettext('Model type')"
              :rules="[selectFieldRequiredRule]"
            ></v-select>
            <v-textarea
              v-model="editedDevice.internalNotes"
              :label="$gettext('Internal Notes')"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closeEditDialog">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!editFormValid"
            @click="saveChanges"
            >{{ $gettext('Save') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ $gettext('Confirm Deletion') }}
        </v-card-title>
        <v-card-text>
          <p>
            {{ $gettext('Are you sure you want to delete this edge device?') }}
          </p>
          <p>
            <strong>{{ deviceToDelete?.name }}</strong>
          </p>
          <p>
            <strong>{{ deviceToDelete?.serialNumber }}</strong>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" variant="text" @click="closeDeleteDialog">{{
            $gettext('Cancel')
          }}</v-btn>
          <v-spacer />
          <v-btn color="error" variant="text" @click="confirmDelete">{{
            $gettext('Delete')
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
